/**
 * The wizard's third step, Estimate (docs/plans/lms-course-builder-plan.md
 * 8.2 step 3, 6.3, 6.4, 6.12, 9.3; WP-B30). The run was created when the
 * admin left Steer (POST /admin/builder/runs); this step polls GET
 * /admin/builder/runs/:id every 5 seconds until the first job has read the
 * files and estimated (status estimated) or refused them (failed), then shows
 * each file (pages and tokens, or the refusal with its reason), the planned
 * sections, the two models, the estimated cost in dollars and the room left
 * under the daily and monthly caps, and Start. Nothing has been spent: reading
 * the files makes no AI call.
 *
 * Start (POST /runs/:id/start) re-checks the switch, the rate limit and the
 * caps before anything is spent; a refusal shows the code's plain words and
 * no run starts. A refused source or a cap the estimate would cross shows the
 * reason and no Start.
 */
import { useState } from 'react';
import { ArrowLeft, Loader2, Play } from 'lucide-react';
import { primaryBtnCls, secondaryBtnCls } from '../../../../../ui/styles';
import { formatCents, readBuilderError, startBuilderRun } from '../../../../api/builder';
import type { BuilderRun, BuilderSource } from '../../../../api/builder';
import { noticeCls, pillCls } from '../../../../components/player/styles';
import { cardCls, hintCls, microLabelCls, tableWrapCls, tdCls, thCls } from '../styles';
import { capProblem, dollars, explainError, fileSizeLabel, isReadingStatus } from './runModel';
import { useBuildRun } from './useBuildRun';

export interface EstimateStepProps {
  runId: string;
  /** Start answered: the run is queued. */
  onStarted: (run: BuilderRun) => void;
  /** Back to Steer (the wizard cancels this run before it goes back). */
  onBack: () => void;
  /** Poll interval (tests); default 5 s. */
  intervalMs?: number;
}

function SourceStatus({ source }: { source: BuilderSource }) {
  if (source.extraction_status === 'done') {
    const parts: string[] = [];
    if (typeof source.page_count === 'number') parts.push(`${source.page_count} page${source.page_count === 1 ? '' : 's'}`);
    if (typeof source.token_estimate === 'number') parts.push(`about ${source.token_estimate.toLocaleString()} tokens`);
    return <span className={pillCls('green')}>{parts.length ? `Read: ${parts.join(', ')}` : 'Read'}</span>;
  }
  if (source.extraction_status === 'refused' || source.extraction_status === 'failed') {
    return (
      <span className="space-y-1">
        <span className={pillCls('red')}>{source.extraction_status === 'refused' ? 'Refused' : 'Could not be read'}</span>
        {source.extraction_note ? <span className="block text-xs text-red-700 dark:text-red-400">{source.extraction_note}</span> : null}
      </span>
    );
  }
  return <span className={pillCls('gray')}>Waiting to be read</span>;
}

export function EstimateStep({ runId, onStarted, onBack, intervalMs }: EstimateStepProps) {
  const { detail, error } = useBuildRun(runId, { intervalMs, stopWhen: (d) => d.run.status === 'estimated' });
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const run = detail?.run ?? null;
  const estimate = detail?.estimate ?? run?.estimate ?? null;
  const sources = detail?.sources ?? [];
  const cap = capProblem(estimate);
  const refused = sources.filter((s) => s.extraction_status === 'refused' || s.extraction_status === 'failed');

  const start = async () => {
    if (!run || starting) return;
    setStarting(true);
    setStartError(null);
    try {
      const next = await startBuilderRun(run.id);
      onStarted(next);
    } catch (err) {
      const problem = readBuilderError(err, 'The build could not be started.');
      setStartError(explainError(problem.code, problem.message));
      setStarting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="lms-builder-estimate">
      {error && !detail ? (
        <div className={noticeCls('red')} role="alert" data-testid="lms-builder-estimate-error">
          {explainError(error.code, error.message)}
        </div>
      ) : null}

      {!run || isReadingStatus(run.status) ? (
        <div className="flex items-center gap-2 py-6 text-sm text-gray-600 dark:text-gray-300" role="status" data-testid="lms-builder-reading">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Reading the files and working out the cost. Nothing is spent until you press Start.
        </div>
      ) : null}

      {run && run.status === 'failed' ? (
        <div className={noticeCls('red')} role="alert" data-testid="lms-builder-estimate-failed">
          <span>
            {explainError(run.error_code, run.error)}
          </span>
        </div>
      ) : null}

      {run && run.status === 'cancelled' ? (
        <div className={noticeCls('amber')} role="status">
          This run was cancelled. Go back to change the files or the steering and get a new estimate.
        </div>
      ) : null}

      {sources.length > 0 ? (
        <div className={tableWrapCls}>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-testid="lms-builder-estimate-sources">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th scope="col" className={thCls}>
                  Source
                </th>
                <th scope="col" className={thCls}>
                  Read
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {sources.map((s) => (
                <tr key={s.id} data-testid={`lms-builder-estimate-source-${s.ordinal}`}>
                  <td className={tdCls}>
                    <div className="font-medium">{s.title || s.filename}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {s.public_url ? s.public_url : fileSizeLabel(s.size_bytes)}
                    </div>
                  </td>
                  <td className={tdCls}>
                    <SourceStatus source={s} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {run && run.status === 'estimated' && estimate ? (
        <section className={`${cardCls} space-y-3`} aria-label="The estimate" data-testid="lms-builder-estimate-card">
          <dl className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <dt className={microLabelCls}>Estimated cost</dt>
              <dd className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100" data-testid="lms-builder-estimate-cents">
                {formatCents(estimate.cents)}
              </dd>
            </div>
            <div>
              <dt className={microLabelCls}>Sections planned</dt>
              <dd className="text-sm text-gray-900 dark:text-gray-100" data-testid="lms-builder-estimate-sections">
                {`${estimate.sections_planned}`}
                {typeof estimate.pages === 'number' ? <span className="text-xs text-gray-500 dark:text-gray-400">{` from ${estimate.pages} pages`}</span> : null}
              </dd>
            </div>
            <div>
              <dt className={microLabelCls}>Source text</dt>
              <dd className="text-sm tabular-nums text-gray-900 dark:text-gray-100">{`about ${Number(estimate.source_tokens || 0).toLocaleString()} tokens`}</dd>
            </div>
            <div>
              <dt className={microLabelCls}>Models</dt>
              <dd className="break-all text-xs text-gray-700 dark:text-gray-300" data-testid="lms-builder-estimate-models">
                {`Writing: ${estimate.model_author}`}
                <br />
                {`Planning: ${estimate.model_extract}`}
              </dd>
            </div>
          </dl>
          {estimate.caps ? (
            <p className={hintCls} data-testid="lms-builder-estimate-caps">
              {`Room left: ${dollars(estimate.caps.daily_remaining)} today for your network, ${dollars(estimate.caps.monthly_remaining)} this month; a run stops at ${dollars(estimate.caps.run)}.`}
            </p>
          ) : null}
          <p className={hintCls}>
            The estimate is what the spending caps judge. The builder writes a draft only: you read it and publish it yourself.
          </p>
        </section>
      ) : null}

      {cap ? (
        <div className={noticeCls('amber')} role="alert" data-testid="lms-builder-estimate-cap">
          {cap}
        </div>
      ) : null}

      {refused.length > 0 && run?.status !== 'failed' ? (
        <div className={noticeCls('red')} role="alert">
          {`${refused.length} file${refused.length === 1 ? ' was' : 's were'} refused; go back and remove ${refused.length === 1 ? 'it' : 'them'}.`}
        </div>
      ) : null}

      {startError ? (
        <div className={noticeCls('red')} role="alert" data-testid="lms-builder-start-error">
          {startError}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
        <button type="button" className={secondaryBtnCls} onClick={onBack} disabled={starting} data-testid="lms-builder-estimate-back">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Change the files or steering
        </button>
        {run && run.status === 'estimated' && !cap && refused.length === 0 ? (
          <button type="button" className={primaryBtnCls} onClick={start} disabled={starting} data-testid="lms-builder-start">
            {starting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
            {estimate ? `Start (about ${formatCents(estimate.cents)})` : 'Start'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default EstimateStep;
